import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PublicSlimHeader from '../../Common/PublicSlimHeader';
import { getPublicEventFeedbackContext, submitEventFeedback } from '../../../services/eventApi';
import EventLoadingState from '../Common/EventLoadingState';

const FEEDBACK_TYPES = [
  { value: 'PARTICIPANT', label: 'Participant / applicant' },
  { value: 'JUDGE', label: 'Judge' },
  { value: 'ATTENDEE', label: 'Attendee' },
  { value: 'GENERAL', label: 'Other' },
];

const StarRating = ({ label, value, onChange, disabled }) => (
  <div>
    <div className="text-sm font-medium text-gray-700 mb-2">{label}</div>
    <div className="flex flex-wrap gap-2">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onClick={() => onChange(n)}
          className={`w-10 h-10 rounded-lg text-sm font-semibold border transition-colors ${
            value === n
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-300'
          } disabled:opacity-50`}
        >
          {n}
        </button>
      ))}
    </div>
  </div>
);

const PublicEventFeedback = () => {
  const { eventId } = useParams();
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState({ eventName: '', feedbackOpen: false });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    feedbackType: 'PARTICIPANT',
    isAnonymous: false,
    responderName: '',
    responderEmail: '',
    overallRating: null,
    organizationRating: null,
    venueRating: null,
    comments: '',
  });

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    getPublicEventFeedbackContext(eventId)
      .then((res) => {
        setContext(res.data?.data || { eventName: '', feedbackOpen: false });
      })
      .catch(() => {
        setContext({ eventName: '', feedbackOpen: false });
      })
      .finally(() => setLoading(false));
  }, [eventId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.overallRating) {
      toast.error('Please rate your overall experience');
      return;
    }
    if (!form.isAnonymous && !String(form.responderName || '').trim()) {
      toast.error('Please enter your name or choose anonymous feedback');
      return;
    }

    setBusy(true);
    try {
      await submitEventFeedback(eventId, {
        feedbackType: form.feedbackType,
        isAnonymous: form.isAnonymous,
        responderName: form.isAnonymous ? '' : form.responderName.trim(),
        responderEmail: form.isAnonymous ? '' : form.responderEmail.trim(),
        overallRating: form.overallRating,
        organizationRating: form.organizationRating || null,
        venueRating: form.venueRating || null,
        comments: form.comments.trim(),
      });
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not submit feedback');
    } finally {
      setBusy(false);
    }
  };

  const title = context.eventName || 'Event feedback';

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-xl mx-auto">
        <PublicSlimHeader title={title} subtitle="Share your experience" />

        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200">
            <EventLoadingState message="Loading feedback..." />
          </div>
        ) : !context.feedbackOpen ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <h2 className="text-lg font-semibold text-gray-900">Feedback not available</h2>
            <p className="text-sm text-gray-500 mt-2">
              Feedback opens after the event programme has concluded and results have been published.
            </p>
          </div>
        ) : submitted ? (
          <div className="bg-white rounded-xl border border-emerald-200 bg-emerald-50/40 p-8 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 mb-4">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Thank you</h2>
            <p className="text-sm text-gray-600 mt-2">Your feedback has been recorded. We appreciate you taking the time to respond.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">I am a…</label>
              <select
                value={form.feedbackType}
                onChange={(e) => setForm((f) => ({ ...f, feedbackType: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={busy}
              >
                {FEEDBACK_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.isAnonymous}
                onChange={(e) => setForm((f) => ({ ...f, isAnonymous: e.target.checked }))}
                disabled={busy}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Submit anonymously
            </label>

            {!form.isAnonymous && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your name</label>
                  <input
                    type="text"
                    value={form.responderName}
                    onChange={(e) => setForm((f) => ({ ...f, responderName: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={busy}
                    maxLength={120}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
                  <input
                    type="email"
                    value={form.responderEmail}
                    onChange={(e) => setForm((f) => ({ ...f, responderEmail: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={busy}
                    maxLength={200}
                  />
                </div>
              </div>
            )}

            <StarRating
              label="Overall experience *"
              value={form.overallRating}
              onChange={(n) => setForm((f) => ({ ...f, overallRating: n }))}
              disabled={busy}
            />
            <StarRating
              label="Organization (optional)"
              value={form.organizationRating}
              onChange={(n) => setForm((f) => ({ ...f, organizationRating: n }))}
              disabled={busy}
            />
            <StarRating
              label="Venue (optional)"
              value={form.venueRating}
              onChange={(n) => setForm((f) => ({ ...f, venueRating: n }))}
              disabled={busy}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Comments (optional)</label>
              <textarea
                value={form.comments}
                onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={busy}
                maxLength={2000}
                placeholder="What went well? What could be improved?"
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {busy ? 'Submitting…' : 'Submit feedback'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PublicEventFeedback;
